/**
 * v2 Design System — Color Tokens
 *
 * `colorValues`         — raw CSS variable expressions and Tailwind classes per color.
 *                         Import these to build component-specific class strings for any new component.
 *
 * `buttonVariantClasses` — pre-composed class strings for <Button> compound variants.
 *                          These reference --btn-bg / --btn-hover-bg CSS custom properties.
 *                          When building other components, create a similar map using your
 *                          own --component-bg variable names and the colorValues above.
 */

export type ColorValues = {
  // ── Filled (solid background) ───────────────────────────────────────────
  bg: string;
  bgHover: string;
  bgDark: string;
  bgDarkHover: string;
  /** Text class for content sitting on a filled background (usually 'text-white') */
  textOnFilled: string;
  ring: string;

  // ── Tinted / subtle (light-wash background) ─────────────────────────────
  bgTint: string;
  bgTintHover: string;
  bgTintDark: string;
  bgTintDarkHover: string;
  textOnTint: string;
  textOnTintDark: string;

  // ── Outline / ghost (transparent background) ────────────────────────────
  /** Hover background for elements that start transparent */
  bgHoverLight: string;
  bgHoverLightDark: string;
  borderClass: string;
  borderDarkClass: string;
  colorTextClass: string;
  colorTextDarkClass: string;
};

export const colorValues = {
  primary: {
    bg: 'var(--color-green-600)',
    bgHover: 'var(--color-green-700)',
    bgDark: 'var(--color-green-500)',
    bgDarkHover: 'var(--color-green-600)',
    textOnFilled: 'text-white',
    ring: 'focus-visible:ring-green-500',
    bgTint: 'var(--color-green-50)',
    bgTintHover: 'var(--color-green-100)',
    bgTintDark: 'var(--color-green-900)',
    bgTintDarkHover: 'var(--color-green-800)',
    textOnTint: 'text-green-700',
    textOnTintDark: 'dark:text-green-300',
    bgHoverLight: 'var(--color-green-50)',
    bgHoverLightDark: 'var(--color-green-900)',
    borderClass: 'border-green-600',
    borderDarkClass: 'dark:border-green-400',
    colorTextClass: 'text-green-700',
    colorTextDarkClass: 'dark:text-green-400'
  },
  secondary: {
    bg: 'var(--color-blue-600)',
    bgHover: 'var(--color-blue-700)',
    bgDark: 'var(--color-blue-500)',
    bgDarkHover: 'var(--color-blue-600)',
    textOnFilled: 'text-white',
    ring: 'focus-visible:ring-blue-500',
    bgTint: 'var(--color-blue-50)',
    bgTintHover: 'var(--color-blue-100)',
    bgTintDark: 'var(--color-blue-900)',
    bgTintDarkHover: 'var(--color-blue-800)',
    textOnTint: 'text-blue-700',
    textOnTintDark: 'dark:text-blue-300',
    bgHoverLight: 'var(--color-blue-50)',
    bgHoverLightDark: 'var(--color-blue-900)',
    borderClass: 'border-blue-600',
    borderDarkClass: 'dark:border-blue-400',
    colorTextClass: 'text-blue-700',
    colorTextDarkClass: 'dark:text-blue-400'
  },
  destructive: {
    bg: 'var(--color-uniform-red-600)',
    bgHover: 'var(--color-uniform-red-700)',
    bgDark: 'var(--color-uniform-red-500)',
    bgDarkHover: 'var(--color-uniform-red-600)',
    textOnFilled: 'text-white',
    ring: 'focus-visible:ring-uniform-red-500',
    bgTint: 'var(--color-uniform-red-50)',
    bgTintHover: 'var(--color-uniform-red-100)',
    bgTintDark: 'var(--color-uniform-red-900)',
    bgTintDarkHover: 'var(--color-uniform-red-800)',
    textOnTint: 'text-uniform-red-700',
    textOnTintDark: 'dark:text-uniform-red-300',
    bgHoverLight: 'var(--color-uniform-red-50)',
    bgHoverLightDark: 'var(--color-uniform-red-900)',
    borderClass: 'border-uniform-red-500',
    borderDarkClass: 'dark:border-uniform-red-400',
    colorTextClass: 'text-uniform-red-700',
    colorTextDarkClass: 'dark:text-uniform-red-400'
  },
  warning: {
    // Aligned with the app's Banner/Alert warning style (bg-yellow-100, text-yellow-900)
    bg: 'var(--color-yellow-500)',
    bgHover: 'var(--color-yellow-600)',
    bgDark: 'var(--color-yellow-400)',
    bgDarkHover: 'var(--color-yellow-500)',
    textOnFilled: 'text-yellow-950',
    ring: 'focus-visible:ring-yellow-500',
    bgTint: 'var(--color-yellow-100)',
    bgTintHover: 'var(--color-yellow-200)',
    bgTintDark: 'var(--color-yellow-900)',
    bgTintDarkHover: 'var(--color-yellow-800)',
    textOnTint: 'text-yellow-900',
    textOnTintDark: 'dark:text-yellow-200',
    bgHoverLight: 'var(--color-yellow-50)',
    bgHoverLightDark: 'var(--color-yellow-900)',
    borderClass: 'border-yellow-500',
    borderDarkClass: 'dark:border-yellow-400',
    colorTextClass: 'text-yellow-800',
    colorTextDarkClass: 'dark:text-yellow-300'
  },
  neutral: {
    // uniform-gray-600 (#52647A) — blue-gray, closer to the original Color.GRAY secondary
    bg: 'var(--color-uniform-gray-600)',
    bgHover: 'var(--color-uniform-gray-700)',
    bgDark: 'var(--color-uniform-gray-500)',
    bgDarkHover: 'var(--color-uniform-gray-600)',
    textOnFilled: 'text-white',
    ring: 'focus-visible:ring-uniform-gray-500',
    bgTint: 'var(--color-uniform-gray-100)',
    bgTintHover: 'var(--color-uniform-gray-200)',
    bgTintDark: 'var(--color-uniform-gray-800)',
    bgTintDarkHover: 'var(--color-uniform-gray-700)',
    textOnTint: 'text-uniform-gray-700',
    textOnTintDark: 'dark:text-uniform-gray-300',
    bgHoverLight: 'var(--color-uniform-gray-50)',
    bgHoverLightDark: 'var(--color-uniform-gray-800)',
    borderClass: 'border-uniform-gray-500',
    borderDarkClass: 'dark:border-uniform-gray-400',
    colorTextClass: 'text-uniform-gray-600',
    colorTextDarkClass: 'dark:text-uniform-gray-300'
  }
} satisfies Record<string, ColorValues>;

// ── Pre-composed Pill color class strings ────────────────────────────────────
// Three variants per color matching Button filled / subtle / outline.

export const pillColorClasses = {
  neutral: {
    filled: 'bg-uniform-gray-600 text-white dark:bg-uniform-gray-500',
    subtle:
      'bg-uniform-gray-100 text-uniform-gray-700 dark:bg-uniform-gray-800 dark:text-uniform-gray-300',
    outline:
      'border border-uniform-gray-400 text-uniform-gray-600 dark:border-uniform-gray-500 dark:text-uniform-gray-300'
  },
  primary: {
    filled: 'bg-green-600 text-white dark:bg-green-500',
    subtle: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
    outline: 'border border-green-600 text-green-700 dark:border-green-400 dark:text-green-400'
  },
  secondary: {
    filled: 'bg-blue-600 text-white dark:bg-blue-500',
    subtle: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    outline: 'border border-blue-600 text-blue-700 dark:border-blue-400 dark:text-blue-400'
  },
  destructive: {
    filled: 'bg-uniform-red-600 text-white dark:bg-uniform-red-500',
    subtle:
      'bg-uniform-red-100 text-uniform-red-700 dark:bg-uniform-red-900 dark:text-uniform-red-300',
    outline:
      'border border-uniform-red-500 text-uniform-red-700 dark:border-uniform-red-400 dark:text-uniform-red-400'
  },
  warning: {
    filled: 'bg-yellow-500 text-yellow-950 dark:bg-yellow-400',
    subtle: 'bg-yellow-100 text-yellow-900 dark:bg-yellow-900 dark:text-yellow-200',
    outline: 'border border-yellow-500 text-yellow-800 dark:border-yellow-400 dark:text-yellow-300'
  }
} as const;

// ── Pre-composed Toggle color class strings ───────────────────────────────────
// Applied to the track slot: off state is always gray, on state is the color.

export const toggleColorClasses = {
  neutral: {
    track:
      'data-[checked]:bg-uniform-gray-600 focus-visible:ring-uniform-gray-500 dark:data-[checked]:bg-uniform-gray-500'
  },
  primary: {
    track:
      'data-[checked]:bg-green-600 focus-visible:ring-green-500 dark:data-[checked]:bg-green-500'
  },
  secondary: {
    track: 'data-[checked]:bg-blue-600 focus-visible:ring-blue-500 dark:data-[checked]:bg-blue-500'
  },
  destructive: {
    track:
      'data-[checked]:bg-uniform-red-600 focus-visible:ring-uniform-red-500 dark:data-[checked]:bg-uniform-red-500'
  },
  warning: {
    track:
      'data-[checked]:bg-yellow-500 focus-visible:ring-yellow-500 dark:data-[checked]:bg-yellow-400'
  }
} as const;

// ── Pre-composed Accordion color class strings ────────────────────────────────
// `base`    → color variant slots (trigger / content / icon)
// `default` → compound variant: default bordered card
// `ghost`   → compound variant: ghost divider-only
// `subtle`  → compound variant: tinted bg + colored text
// `filled`  → compound variant: solid colored header + tinted content

export const accordionColorClasses = {
  neutral: {
    base: {
      trigger: [
        'text-uniform-gray-900 hover:bg-uniform-gray-50 focus-visible:ring-uniform-gray-400',
        'dark:text-uniform-gray-100 dark:hover:bg-uniform-gray-800'
      ],
      content: 'text-uniform-gray-600 dark:text-uniform-gray-400',
      icon: 'text-uniform-gray-500 dark:text-uniform-gray-400'
    },
    default: {
      item: 'border-uniform-gray-200 dark:border-uniform-gray-700',
      content: 'border-uniform-gray-100 dark:border-uniform-gray-700'
    },
    ghost: { root: 'divide-uniform-gray-200 dark:divide-uniform-gray-700' },
    subtle: {
      item: 'border-uniform-gray-200 dark:border-uniform-gray-700',
      trigger:
        'bg-uniform-gray-100 hover:bg-uniform-gray-200 text-uniform-gray-700 focus-visible:ring-uniform-gray-400 dark:bg-uniform-gray-800 dark:hover:bg-uniform-gray-700 dark:text-uniform-gray-300',
      content:
        'border-uniform-gray-200 text-uniform-gray-700 dark:border-uniform-gray-700 dark:text-uniform-gray-300',
      icon: 'text-uniform-gray-500 dark:text-uniform-gray-400'
    },
    filled: {
      trigger:
        'bg-uniform-gray-600 hover:bg-uniform-gray-700 text-white focus-visible:ring-uniform-gray-500 dark:bg-uniform-gray-500 dark:hover:bg-uniform-gray-600',
      content:
        'bg-uniform-gray-50 text-uniform-gray-700 dark:bg-uniform-gray-900 dark:text-uniform-gray-300',
      icon: 'text-white'
    }
  },
  primary: {
    base: {
      trigger: [
        'text-green-900 hover:bg-green-50 focus-visible:ring-green-400',
        'dark:text-green-100 dark:hover:bg-green-900/40'
      ],
      content: 'text-green-800 dark:text-green-300',
      icon: 'text-green-600 dark:text-green-400'
    },
    default: {
      item: 'border-green-400 dark:border-green-800',
      content: 'border-green-300 dark:border-green-800'
    },
    ghost: { root: 'divide-green-400 dark:divide-green-800' },
    subtle: {
      item: 'border-green-400 dark:border-green-800',
      trigger:
        'bg-green-50 hover:bg-green-100 text-green-700 focus-visible:ring-green-400 dark:bg-green-900 dark:hover:bg-green-800 dark:text-green-300',
      content: 'border-green-400 text-green-700 dark:border-green-800 dark:text-green-300',
      icon: 'text-green-500 dark:text-green-400'
    },
    filled: {
      trigger:
        'bg-green-600 hover:bg-green-700 text-white focus-visible:ring-green-500 dark:bg-green-500 dark:hover:bg-green-600',
      content: 'bg-green-50 text-green-900 dark:bg-green-950 dark:text-green-200',
      icon: 'text-white'
    }
  },
  secondary: {
    base: {
      trigger: [
        'text-blue-900 hover:bg-blue-50 focus-visible:ring-blue-400',
        'dark:text-blue-100 dark:hover:bg-blue-900/40'
      ],
      content: 'text-blue-800 dark:text-blue-300',
      icon: 'text-blue-600 dark:text-blue-400'
    },
    default: {
      item: 'border-blue-400 dark:border-blue-800',
      content: 'border-blue-300 dark:border-blue-800'
    },
    ghost: { root: 'divide-blue-400 dark:divide-blue-800' },
    subtle: {
      item: 'border-blue-400 dark:border-blue-800',
      trigger:
        'bg-blue-50 hover:bg-blue-100 text-blue-700 focus-visible:ring-blue-400 dark:bg-blue-900 dark:hover:bg-blue-800 dark:text-blue-300',
      content: 'border-blue-400 text-blue-700 dark:border-blue-800 dark:text-blue-300',
      icon: 'text-blue-500 dark:text-blue-400'
    },
    filled: {
      trigger:
        'bg-blue-600 hover:bg-blue-700 text-white focus-visible:ring-blue-500 dark:bg-blue-500 dark:hover:bg-blue-600',
      content: 'bg-blue-50 text-blue-900 dark:bg-blue-950 dark:text-blue-200',
      icon: 'text-white'
    }
  },
  destructive: {
    base: {
      trigger: [
        'text-uniform-red-900 hover:bg-uniform-red-50 focus-visible:ring-uniform-red-400',
        'dark:text-uniform-red-100 dark:hover:bg-uniform-red-900/40'
      ],
      content: 'text-uniform-red-800 dark:text-uniform-red-300',
      icon: 'text-uniform-red-500 dark:text-uniform-red-400'
    },
    default: {
      item: 'border-uniform-red-400 dark:border-uniform-red-800',
      content: 'border-uniform-red-300 dark:border-uniform-red-800'
    },
    ghost: { root: 'divide-uniform-red-400 dark:divide-uniform-red-800' },
    subtle: {
      item: 'border-uniform-red-400 dark:border-uniform-red-800',
      trigger:
        'bg-uniform-red-50 hover:bg-uniform-red-100 text-uniform-red-700 focus-visible:ring-uniform-red-400 dark:bg-uniform-red-900 dark:hover:bg-uniform-red-800 dark:text-uniform-red-300',
      content:
        'border-uniform-red-400 text-uniform-red-700 dark:border-uniform-red-800 dark:text-uniform-red-300',
      icon: 'text-uniform-red-500 dark:text-uniform-red-400'
    },
    filled: {
      trigger:
        'bg-uniform-red-600 hover:bg-uniform-red-700 text-white focus-visible:ring-uniform-red-500 dark:bg-uniform-red-500 dark:hover:bg-uniform-red-600',
      content:
        'bg-uniform-red-50 text-uniform-red-900 dark:bg-uniform-red-950 dark:text-uniform-red-200',
      icon: 'text-white'
    }
  },
  warning: {
    base: {
      trigger: [
        'text-yellow-900 hover:bg-yellow-50 focus-visible:ring-yellow-400',
        'dark:text-yellow-100 dark:hover:bg-yellow-900/40'
      ],
      content: 'text-yellow-800 dark:text-yellow-300',
      icon: 'text-yellow-600 dark:text-yellow-500'
    },
    default: {
      item: 'border-yellow-400 dark:border-yellow-800',
      content: 'border-yellow-300 dark:border-yellow-800'
    },
    ghost: { root: 'divide-yellow-400 dark:divide-yellow-800' },
    subtle: {
      item: 'border-yellow-400 dark:border-yellow-800',
      trigger:
        'bg-yellow-100 hover:bg-yellow-200 text-yellow-900 focus-visible:ring-yellow-400 dark:bg-yellow-900 dark:hover:bg-yellow-800 dark:text-yellow-200',
      content: 'border-yellow-400 text-yellow-800 dark:border-yellow-800 dark:text-yellow-200',
      icon: 'text-yellow-600 dark:text-yellow-400'
    },
    filled: {
      trigger:
        'bg-yellow-500 hover:bg-yellow-600 text-yellow-950 focus-visible:ring-yellow-500 dark:bg-yellow-400 dark:hover:bg-yellow-500',
      content: 'bg-yellow-50 text-yellow-900 dark:bg-yellow-950 dark:text-yellow-200',
      icon: 'text-yellow-950 dark:text-yellow-950'
    }
  }
} as const;

// ── Pre-composed PillSwitch color class strings ──────────────────────────────
// Pattern: solid colored container, white/75 unselected text, white card selected.
// Warning uses yellow-950 text (yellow is too light for white text contrast).

export const pillSwitchColorClasses = {
  neutral: {
    root: 'bg-uniform-gray-200 dark:bg-uniform-gray-700',
    option: [
      'text-uniform-gray-700 hover:text-uniform-gray-800',
      'dark:text-uniform-gray-200 dark:hover:text-uniform-gray-100',
      'focus-visible:ring-uniform-gray-400',
      'data-[checked]:bg-white data-[checked]:shadow-xs data-[checked]:text-uniform-gray-900',
      'dark:data-[checked]:bg-uniform-gray-900 dark:data-[checked]:text-uniform-gray-100'
    ]
  },
  primary: {
    root: 'bg-green-600 dark:bg-green-500',
    option: [
      'text-white/75 hover:text-white',
      'focus-visible:ring-white/50',
      'data-[checked]:bg-white data-[checked]:shadow-xs data-[checked]:text-green-900'
    ]
  },
  secondary: {
    root: 'bg-blue-600 dark:bg-blue-500',
    option: [
      'text-white/75 hover:text-white',
      'focus-visible:ring-white/50',
      'data-[checked]:bg-white data-[checked]:shadow-xs data-[checked]:text-blue-900'
    ]
  },
  destructive: {
    root: 'bg-uniform-red-600 dark:bg-uniform-red-500',
    option: [
      'text-white/75 hover:text-white',
      'focus-visible:ring-white/50',
      'data-[checked]:bg-white data-[checked]:shadow-xs data-[checked]:text-uniform-red-900'
    ]
  },
  warning: {
    root: 'bg-yellow-500 dark:bg-yellow-400',
    option: [
      'text-yellow-950/75 hover:text-yellow-950',
      'focus-visible:ring-yellow-700',
      'data-[checked]:bg-white data-[checked]:shadow-xs data-[checked]:text-yellow-900'
    ]
  }
} as const;

// ── Pre-composed Button compound variant class strings ───────────────────────
// These use --btn-bg / --btn-hover-bg CSS custom properties.
// When creating other components, follow the same pattern with your own
// --component-bg variable names, referencing colorValues above for the values.

export const buttonVariantClasses = {
  primary: {
    default:
      '[--btn-bg:var(--color-green-600)] [--btn-hover-bg:var(--color-green-700)] dark:[--btn-bg:var(--color-green-500)] dark:[--btn-hover-bg:var(--color-green-600)] text-white focus-visible:ring-green-500 border-black/25 dark:border-white/25',
    outline:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-green-50)] dark:[--btn-hover-bg:var(--color-green-900)] border-green-600 text-green-700 focus-visible:ring-green-500 dark:border-green-400 dark:text-green-400',
    ghost:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-green-50)] dark:[--btn-hover-bg:var(--color-green-900)] text-green-700 focus-visible:ring-green-500 dark:text-green-400',
    quiet:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-green-50)] dark:[--btn-hover-bg:var(--color-green-900)] text-uniform-gray-400 hover:text-green-700 focus-visible:ring-green-500 dark:text-uniform-gray-500 dark:hover:text-green-400',
    subtle:
      '[--btn-bg:var(--color-green-50)] [--btn-hover-bg:var(--color-green-100)] dark:[--btn-bg:var(--color-green-900)] dark:[--btn-hover-bg:var(--color-green-800)] text-green-700 focus-visible:ring-green-500 dark:text-green-300'
  },
  secondary: {
    default:
      '[--btn-bg:var(--color-blue-600)] [--btn-hover-bg:var(--color-blue-700)] dark:[--btn-bg:var(--color-blue-500)] dark:[--btn-hover-bg:var(--color-blue-600)] text-white focus-visible:ring-blue-500 border-black/25 dark:border-white/25',
    outline:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-blue-50)] dark:[--btn-hover-bg:var(--color-blue-900)] border-blue-600 text-blue-700 focus-visible:ring-blue-500 dark:border-blue-400 dark:text-blue-400',
    ghost:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-blue-50)] dark:[--btn-hover-bg:var(--color-blue-900)] text-blue-700 focus-visible:ring-blue-500 dark:text-blue-400',
    quiet:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-blue-50)] dark:[--btn-hover-bg:var(--color-blue-900)] text-uniform-gray-400 hover:text-blue-700 focus-visible:ring-blue-500 dark:text-uniform-gray-500 dark:hover:text-blue-400',
    subtle:
      '[--btn-bg:var(--color-blue-50)] [--btn-hover-bg:var(--color-blue-100)] dark:[--btn-bg:var(--color-blue-900)] dark:[--btn-hover-bg:var(--color-blue-800)] text-blue-700 focus-visible:ring-blue-500 dark:text-blue-300'
  },
  destructive: {
    default:
      '[--btn-bg:var(--color-uniform-red-600)] [--btn-hover-bg:var(--color-uniform-red-700)] dark:[--btn-bg:var(--color-uniform-red-500)] dark:[--btn-hover-bg:var(--color-uniform-red-600)] text-white focus-visible:ring-uniform-red-500 border-black/25 dark:border-white/25',
    outline:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-uniform-red-50)] dark:[--btn-hover-bg:var(--color-uniform-red-900)] border-uniform-red-500 text-uniform-red-700 focus-visible:ring-uniform-red-500 dark:border-uniform-red-400 dark:text-uniform-red-400',
    ghost:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-uniform-red-50)] dark:[--btn-hover-bg:var(--color-uniform-red-900)] text-uniform-red-700 focus-visible:ring-uniform-red-500 dark:text-uniform-red-400',
    quiet:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-uniform-red-50)] dark:[--btn-hover-bg:var(--color-uniform-red-900)] text-uniform-gray-400 hover:text-uniform-red-700 focus-visible:ring-uniform-red-500 dark:text-uniform-gray-500 dark:hover:text-uniform-red-400',
    subtle:
      '[--btn-bg:var(--color-uniform-red-50)] [--btn-hover-bg:var(--color-uniform-red-100)] dark:[--btn-bg:var(--color-uniform-red-900)] dark:[--btn-hover-bg:var(--color-uniform-red-800)] text-uniform-red-700 focus-visible:ring-uniform-red-500 dark:text-uniform-red-300'
  },
  warning: {
    default:
      '[--btn-bg:var(--color-yellow-500)] [--btn-hover-bg:var(--color-yellow-600)] dark:[--btn-bg:var(--color-yellow-400)] dark:[--btn-hover-bg:var(--color-yellow-500)] text-yellow-950 focus-visible:ring-yellow-500 border-black/25 dark:border-white/25',
    outline:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-yellow-50)] dark:[--btn-hover-bg:var(--color-yellow-900)] border-yellow-500 text-yellow-800 focus-visible:ring-yellow-500 dark:border-yellow-400 dark:text-yellow-300',
    ghost:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-yellow-50)] dark:[--btn-hover-bg:var(--color-yellow-900)] text-yellow-800 focus-visible:ring-yellow-500 dark:text-yellow-300',
    quiet:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-yellow-50)] dark:[--btn-hover-bg:var(--color-yellow-900)] text-uniform-gray-400 hover:text-yellow-800 focus-visible:ring-yellow-500 dark:text-uniform-gray-500 dark:hover:text-yellow-300',
    subtle:
      '[--btn-bg:var(--color-yellow-100)] [--btn-hover-bg:var(--color-yellow-200)] dark:[--btn-bg:var(--color-yellow-900)] dark:[--btn-hover-bg:var(--color-yellow-800)] text-yellow-900 focus-visible:ring-yellow-500 dark:text-yellow-200'
  },
  neutral: {
    default:
      '[--btn-bg:var(--color-uniform-gray-600)] [--btn-hover-bg:var(--color-uniform-gray-700)] dark:[--btn-bg:var(--color-uniform-gray-500)] dark:[--btn-hover-bg:var(--color-uniform-gray-600)] text-white focus-visible:ring-uniform-gray-500 border-black/25 dark:border-white/25',
    outline:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-uniform-gray-100)] dark:[--btn-hover-bg:var(--color-uniform-gray-800)] border-uniform-gray-600 text-uniform-gray-600 focus-visible:ring-uniform-gray-500 dark:border-uniform-gray-400 dark:text-uniform-gray-300',
    ghost:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-uniform-gray-100)] dark:[--btn-hover-bg:var(--color-uniform-gray-800)] text-uniform-gray-600 focus-visible:ring-uniform-gray-500 dark:text-uniform-gray-300',
    quiet:
      '[--btn-bg:transparent] [--btn-hover-bg:var(--color-uniform-gray-50)] dark:[--btn-hover-bg:var(--color-uniform-gray-900)] text-uniform-gray-400 hover:text-uniform-gray-700 focus-visible:ring-uniform-gray-500 dark:text-uniform-gray-500 dark:hover:text-uniform-gray-200',
    subtle:
      '[--btn-bg:var(--color-uniform-gray-100)] [--btn-hover-bg:var(--color-uniform-gray-200)] dark:[--btn-bg:var(--color-uniform-gray-800)] dark:[--btn-hover-bg:var(--color-uniform-gray-700)] text-uniform-gray-600 focus-visible:ring-uniform-gray-500 dark:text-uniform-gray-300'
  }
} as const;
